import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const DOC_GEN_STEPS = [
  { key: "queued", label: "Queued" },
  { key: "template_download", label: "Downloading Templates..." },
  { key: "doc_fill", label: "Filling Documents..." },
  { key: "pdf_conversion", label: "Converting to PDF..." },
  { key: "merge", label: "Merging Documents..." },
  { key: "ams", label: "Creating Policy..." },
  { key: "upload_and_notify", label: "Uploading & Notifying..." },
];

function getStepIndex(step) {
  const idx = DOC_GEN_STEPS.findIndex((s) => s.key === step);
  return idx === -1 ? 0 : idx;
}

function getDocGenDotColor(i, activeIdx) {
  if (i < activeIdx) return "success.main";
  if (i === activeIdx) return "info.main";
  return "grey.300";
}

function DocGenProgress({ step }) {
  const activeIdx = getStepIndex(step);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {DOC_GEN_STEPS.map((s, i) => (
        <Box
          key={s.key}
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: getDocGenDotColor(i, activeIdx),
            animation: i === activeIdx ? "pulse 1.4s infinite" : "none",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.3 },
            },
          }}
        />
      ))}
    </Stack>
  );
}

DocGenProgress.propTypes = {
  step: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

function formatDuration(minutes) {
  if (minutes == null) return "-";
  if (minutes < 1) return "<1m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function formatApprovedAt(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function rowBondNumber(row) {
  return row.bond_number ?? row.bondNumber ?? "-";
}

function rowName(row) {
  return row.name ?? row.estate_name ?? row.bond_name ?? "-";
}

function rowType(row) {
  return row.bond_type ?? row.type ?? "-";
}

function rowTriggeredBy(row) {
  return row.triggered_by ?? row.triggered_by_name ?? row.approved_by ?? "-";
}

function rowApprovedAt(row) {
  return row.approved_at ?? row.approvedAt;
}

function rowStatus(row) {
  return (row.doc_generation_status ?? row.status ?? "").toString().toLowerCase();
}

function rowDurationMinutes(row) {
  const v = row.minutes_elapsed ?? row.duration_minutes ?? row.duration;
  if (typeof v === "number") return v;
  if (v != null) return Number(v);
  return null;
}

function rowStep(row) {
  return row.current_step ?? row.step ?? "queued";
}

export default function ApproveBondsStatsDialog({
  statsOpen,
  setStatsOpen,
  dataLoading,
  stats,
  formatTime,
  docGenStatuses = [],
}) {
  return (
    <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Approval statistics</DialogTitle>
      <DialogContent>
        {dataLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="body1">
              Total approved bonds: <strong>{stats?.totalApprovedBonds ?? 0}</strong>
            </Typography>
            <Typography variant="body1">
              Average processing time:{" "}
              <strong>{formatTime?.(stats?.averageProcessingTime ?? 0) ?? "—"}</strong>
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Document generation status
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bond #</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Triggered By</TableCell>
                <TableCell>Approved At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docGenStatuses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No document generation jobs in progress.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                docGenStatuses.map((row, idx) => {
                  const st = rowStatus(row);
                  const processing = st === "processing";
                  const completed = st === "completed";
                  const failed = st === "failed";
                  return (
                    <TableRow key={row.id ?? row.bond_id ?? idx}>
                      <TableCell>{rowBondNumber(row)}</TableCell>
                      <TableCell>{rowName(row)}</TableCell>
                      <TableCell>{rowType(row)}</TableCell>
                      <TableCell>{rowTriggeredBy(row)}</TableCell>
                      <TableCell>{formatApprovedAt(rowApprovedAt(row))}</TableCell>
                      <TableCell>
                        {processing && (
                          <Chip
                            size="small"
                            color="info"
                            icon={<CircularProgress size={14} color="inherit" />}
                            label="Processing"
                            sx={{ "& .MuiChip-icon": { ml: 0.5 } }}
                          />
                        )}
                        {completed && (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckCircleIcon />}
                            label="Completed"
                          />
                        )}
                        {failed && (
                          <Chip
                            size="small"
                            color="error"
                            icon={<ErrorOutlineIcon />}
                            label="Failed"
                          />
                        )}
                        {!processing && !completed && !failed && (
                          <Chip size="small" label={(row.status ?? st) || "—"} />
                        )}
                      </TableCell>
                      <TableCell>
                        {processing ? (
                          <DocGenProgress step={rowStep(row)} />
                        ) : (
                          formatDuration(rowDurationMinutes(row))
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setStatsOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

ApproveBondsStatsDialog.propTypes = {
  statsOpen: PropTypes.bool.isRequired,
  setStatsOpen: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool.isRequired,
  stats: PropTypes.object,
  formatTime: PropTypes.func,
  docGenStatuses: PropTypes.array,
};
